import { getRouteApi } from '@tanstack/react-router'
import { useCallback } from 'react'
import { z } from 'zod'

import { ORIGINS } from '@/api/actions'
import { ACTIONS_TABS, ActionsPage, type ActionsSearchState } from '@/features/actions/ActionsPage'
import { registerActionCommands } from '@/features/actions/commands'

/**
 * `/actions?tab=actions|rules|capabilities&q=&category=&event=&origin=&new=action|rule`
 * (docs/SKEL-STORY.md R7, §11.7): the action registry, the world rules and what things can do;
 * `new` opens the manual editor on a blank. Unknown values are dropped by the lenient schema.
 */
const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)
const slug = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/)

export const actionsSearchSchema = z.object({
  tab: lenient(z.enum(ACTIONS_TABS)),
  q: lenient(z.string().max(80)),
  category: lenient(slug),
  event: lenient(slug),
  origin: lenient(z.enum(ORIGINS)),
  new: lenient(z.enum(['action', 'rule'])),
})
export type ActionsSearch = z.infer<typeof actionsSearchSchema>

export function validateActionsSearch(input: Record<string, unknown>): ActionsSearch {
  return actionsSearchSchema.parse(input)
}

// The palette's "Actions" / "World rules" entries (CommandPalette's feature registry).
registerActionCommands()

export const ACTIONS_ROUTE_ID = '/actions' as const
const route = getRouteApi(ACTIONS_ROUTE_ID)

export function ActionsRoutePage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  // Filters and tabs replace the history entry; opening the blank editor (`new`) is a new one, so
  // the browser's Back leaves the editor for the list instead of leaving the page.
  const opening = search.new
  const onSearchChange = useCallback((next: ActionsSearchState) => void navigate({ search: next, replace: !next.new || next.new === opening }), [navigate, opening])
  return <ActionsPage tab={search.tab} q={search.q ?? ''} category={search.category} event={search.event} origin={search.origin} creating={search.new} onSearchChange={onSearchChange} />
}
